import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Users, Inbox, Package, Mail, LayoutDashboard } from "lucide-react";
import { useAuth } from "@/components/pepmuse/AuthContext";
import { Reveal, Eyebrow } from "@/components/pepmuse/Reveal";
import { InquiriesPanel, INQUIRY_STATUS_META } from "@/components/pepmuse/admin/InquiriesPanel";
import { UsersPanel } from "@/components/pepmuse/admin/UsersPanel";
import { OrdersPanel } from "@/components/pepmuse/admin/OrdersPanel";
import { FollowupsPanel } from "@/components/pepmuse/admin/FollowupsPanel";

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "inquiries", label: "Inquiries", icon: Inbox },
  { id: "users", label: "Users", icon: Users },
  { id: "orders", label: "Orders", icon: Package },
  { id: "followups", label: "Follow-ups", icon: Mail },
];

const StatCard = ({ testId, label, value, accent = "#B8A7E8" }) => (
  <div data-testid={testId} className="rounded-2xl border border-white/[0.08] bg-card p-6">
    <p className="text-xs font-bold uppercase tracking-[0.16em] text-paper/40">{label}</p>
    <p className="mt-2 font-display text-4xl font-extrabold tracking-tight" style={{ color: accent }}>
      {value}
    </p>
  </div>
);

const OverviewPanel = () => {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/overview`, { credentials: "include" });
        setStats(res.ok ? await res.json() : null);
      } catch {
        setStats(null);
      }
    })();
  }, []);

  if (stats === null) return <p className="text-sm text-paper/45">Loading…</p>;

  return (
    <div data-testid="admin-overview-panel" className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testId="stat-users" label="Registered users" value={stats.users} />
        <StatCard testId="stat-new-inquiries" label="New inquiries" value={stats.inquiries.new} accent="#8FBFE8" />
        <StatCard testId="stat-active-orders" label="Active orders" value={stats.orders.active} accent="#8FD8B8" />
        <StatCard testId="stat-followups" label="Follow-ups sent" value={stats.followups_sent} accent="#E8CE8F" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-white/[0.08] bg-card p-6">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-paper/40">Inquiry pipeline</p>
          <div className="mt-4 space-y-2.5">
            {Object.entries(INQUIRY_STATUS_META).map(([key, meta]) => {
              const count = stats.inquiries[key] || 0;
              const pct = stats.inquiries.total ? Math.max((count / stats.inquiries.total) * 100, count ? 4 : 0) : 0;
              return (
                <div key={key} data-testid={`pipeline-${key}`} className="flex items-center gap-3">
                  <span className="w-28 shrink-0 text-xs font-bold text-paper/55">{meta.label}</span>
                  <span className="h-2 flex-1 overflow-hidden rounded-full bg-white/[0.07]">
                    <span className="block h-full rounded-full transition-[width] duration-500" style={{ width: `${pct}%`, backgroundColor: meta.color }} />
                  </span>
                  <span className="w-8 shrink-0 text-right text-xs font-bold" style={{ color: meta.color }}>{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl border border-white/[0.08] bg-card p-6">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-paper/40">Package orders</p>
          <div className="mt-4 grid grid-cols-3 gap-3">
            {[
              { key: "reserved", label: "Reserved", color: "#E8CE8F" },
              { key: "active", label: "Active", color: "#B8A7E8" },
              { key: "complete", label: "Complete", color: "#8FD8B8" },
            ].map((s) => (
              <div key={s.key} data-testid={`orders-${s.key}`} className="rounded-xl bg-ink/50 p-4 text-center">
                <p className="font-display text-2xl font-extrabold" style={{ color: s.color }}>{stats.orders[s.key]}</p>
                <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.12em] text-paper/45">{s.label}</p>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-paper/40">
            {stats.inquiries.total} total inquiries · {stats.orders.total} total orders
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-white/[0.08] bg-card p-6" data-testid="traffic-panel">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-paper/40">Where visitors come from</p>
          <p className="text-xs text-paper/35">{stats.traffic.pageviews_30d} pageviews · last 30 days</p>
        </div>
        {stats.traffic.pageviews_30d === 0 ? (
          <p data-testid="traffic-empty" className="mt-4 text-sm text-paper/45">
            No traffic recorded yet — sources appear here as visitors arrive.
          </p>
        ) : (
          <div className="mt-4 grid gap-6 sm:grid-cols-2">
            <div data-testid="traffic-sources">
              <p className="mb-2.5 text-[11px] font-bold uppercase tracking-[0.12em] text-paper/35">Sources</p>
              <div className="space-y-2">
                {Object.entries(stats.traffic.sources).map(([source, count]) => {
                  const pct = Math.max((count / stats.traffic.pageviews_30d) * 100, 3);
                  return (
                    <div key={source} data-testid={`traffic-source-${source}`} className="flex items-center gap-3">
                      <span className="w-28 shrink-0 truncate text-xs font-bold text-paper/55">{source}</span>
                      <span className="h-2 flex-1 overflow-hidden rounded-full bg-white/[0.07]">
                        <span className="block h-full rounded-full bg-[#8FBFE8]" style={{ width: `${pct}%` }} />
                      </span>
                      <span className="w-8 shrink-0 text-right text-xs font-bold text-[#8FBFE8]">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div data-testid="traffic-pages">
              <p className="mb-2.5 text-[11px] font-bold uppercase tracking-[0.12em] text-paper/35">Top pages</p>
              <div className="space-y-2">
                {Object.entries(stats.traffic.pages).map(([path, count]) => (
                  <div key={path} className="flex items-center justify-between gap-3 text-xs">
                    <span className="truncate font-bold text-paper/55">{path}</span>
                    <span className="shrink-0 font-bold text-paper/40">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [followupPrefill, setFollowupPrefill] = useState(null);
  const [profileUserId, setProfileUserId] = useState(null);

  useEffect(() => {
    document.title = "Admin Panel — PepMuse";
    return () => {
      document.title = "PepMuse — Your peptide brand. Ready to launch.";
    };
  }, []);

  const handleFollowUp = useCallback((prefill) => {
    setFollowupPrefill(prefill);
    setTab("followups");
  }, []);

  const handleViewUser = useCallback((userId) => {
    setProfileUserId(userId);
    setTab("users");
  }, []);

  return (
    <section data-testid="admin-page" className="pb-24 pt-[120px] sm:pt-[150px]">
      <div className="mx-auto w-full max-w-5xl px-5 sm:px-8">
        {user === null ? (
          <p className="py-20 text-center text-sm text-paper/45">Loading…</p>
        ) : !user || user.role !== "admin" ? (
          <Reveal>
            <div className="mx-auto max-w-md rounded-3xl border border-white/10 bg-card p-9 text-center">
              <Lock className="mx-auto h-9 w-9 text-lilac" aria-hidden="true" />
              <h1 className="mt-5 font-display text-2xl font-extrabold text-paper">Admin access only.</h1>
              <p className="mt-2 text-sm text-paper/55">
                Sign in with an admin account to open the admin panel.
              </p>
              <Link
                to="/account"
                data-testid="admin-signin-link"
                className="mt-6 inline-flex items-center justify-center rounded-full bg-lilac px-7 py-3 text-sm font-bold text-ink transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-lilac-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac focus-visible:ring-offset-2 focus-visible:ring-offset-card"
              >
                Go to sign in
              </Link>
            </div>
          </Reveal>
        ) : (
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <Eyebrow testId="admin-eyebrow">Admin panel</Eyebrow>
                <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-paper sm:text-4xl">
                  Run the pipeline.
                </h1>
                <p className="mt-2 text-sm text-paper/50">
                  Inquiries, users, orders, and follow-ups — all in one place.
                </p>
              </div>
            </div>

            <div data-testid="admin-tabs" role="tablist" aria-label="Admin sections" className="mt-8 flex flex-wrap gap-2">
              {TABS.map((t) => {
                const Icon = t.icon;
                const active = tab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    data-testid={`admin-tab-${t.id}`}
                    onClick={() => {
                      setTab(t.id);
                      if (t.id !== "users") setProfileUserId(null);
                    }}
                    className={`inline-flex items-center gap-2 rounded-full border px-4.5 px-5 py-2.5 text-sm font-bold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac ${
                      active
                        ? "border-lilac/60 bg-lilac text-ink"
                        : "border-white/10 bg-card text-paper/55 hover:border-lilac/40 hover:text-paper"
                    }`}
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                    {t.label}
                  </button>
                );
              })}
            </div>

            <div className="mt-8">
              {tab === "overview" && <OverviewPanel />}
              {tab === "inquiries" && <InquiriesPanel onFollowUp={handleFollowUp} />}
              {tab === "users" && (
                <UsersPanel
                  profileUserId={profileUserId}
                  onOpenProfile={setProfileUserId}
                  onCloseProfile={() => setProfileUserId(null)}
                />
              )}
              {tab === "orders" && <OrdersPanel />}
              {tab === "followups" && <FollowupsPanel prefill={followupPrefill} onViewUser={handleViewUser} />}
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
